/**
 * useGlobalPdfDrop — Global drag & drop de arquivos em qualquer lugar da tela
 *
 * Janela Única: aceita qualquer tipo de arquivo da allowlist (não só PDFs).
 * Delega para importQueueStore — ZERO processamento inline.
 *
 * - Inativo quando qualquer modal está aberta (via uiStore.openModalCount)
 * - Escape fecha o overlay de drag
 * - Safety timeout: se dragover para por 2s, força idle (evita overlay preso)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useUIStore } from '../stores/uiStore';
import { useImportQueueStore } from '../stores/importQueueStore';
import { isFileAllowed } from '../stores/importQueueStore';
import { bigwaToast } from '../utils/bigwaToast';

type DropState = 'idle' | 'dragging' | 'processing' | 'disambiguate';

const SAFETY_TIMEOUT_MS = 2000;

export function useGlobalPdfDrop() {
  const [dropState, setDropState] = useState<DropState>('idle');
  const dragCounterRef = useRef(0);
  const safetyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isAnyModalOpen = (): boolean => {
    return useUIStore.getState().openModalCount > 0;
  };

  const resetDragState = useCallback(() => {
    dragCounterRef.current = 0;
    if (safetyTimerRef.current) {
      clearTimeout(safetyTimerRef.current);
      safetyTimerRef.current = null;
    }
    setDropState(prev => prev === 'dragging' ? 'idle' : prev);
  }, []);

  const restartSafetyTimer = useCallback(() => {
    if (safetyTimerRef.current) {
      clearTimeout(safetyTimerRef.current);
    }
    safetyTimerRef.current = setTimeout(() => {
      resetDragState();
    }, SAFETY_TIMEOUT_MS);
  }, [resetDragState]);

  // Aceitar qualquer arquivo (não só PDFs) — allowlist validada no drop
  const isFileDrag = (e: DragEvent): boolean => {
    if (!e.dataTransfer) return false;
    if (e.dataTransfer.items) {
      for (let i = 0; i < e.dataTransfer.items.length; i++) {
        if (e.dataTransfer.items[i].kind === 'file') return true;
      }
    }
    return e.dataTransfer.types.includes('Files');
  };

  const handleDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault();
    if (isAnyModalOpen()) return;
    if (!isFileDrag(e)) return;
    dragCounterRef.current++;
    if (dragCounterRef.current === 1) {
      setDropState('dragging');
      restartSafetyTimer();
    }
  }, [restartSafetyTimer]);

  const handleDragOver = useCallback((e: DragEvent) => {
    if (isAnyModalOpen()) return;
    if (!isFileDrag(e)) return;
    e.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy';
    }
    restartSafetyTimer();
  }, [restartSafetyTimer]);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    dragCounterRef.current--;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      if (safetyTimerRef.current) {
        clearTimeout(safetyTimerRef.current);
        safetyTimerRef.current = null;
      }
      setDropState(prev => prev === 'dragging' ? 'idle' : prev);
    }
  }, []);

  const handleDrop = useCallback((e: DragEvent) => {
    e.preventDefault();
    dragCounterRef.current = 0;
    if (safetyTimerRef.current) {
      clearTimeout(safetyTimerRef.current);
      safetyTimerRef.current = null;
    }

    if (isAnyModalOpen()) {
      setDropState('idle');
      return;
    }

    if (!e.dataTransfer?.files?.length) {
      setDropState('idle');
      return;
    }

    // Não interceptar drops dentro do QuickAddModal
    const target = e.target as HTMLElement;
    if (target.closest?.('.quick-add-pdf-zone') || target.closest?.('.quick-add-modal')) {
      setDropState('idle');
      return;
    }

    // Gate: se fila já está ativa, informar pesquisador
    if (useImportQueueStore.getState().isActive()) {
      setDropState('idle');
      bigwaToast.info('Aguarde o processamento atual terminar.');
      return;
    }

    // Filtrar por allowlist
    const allowedFiles: File[] = [];
    for (let i = 0; i < e.dataTransfer.files.length; i++) {
      const file = e.dataTransfer.files[i];
      if (isFileAllowed(file.name)) {
        allowedFiles.push(file);
      }
    }

    if (allowedFiles.length === 0) {
      setDropState('idle');
      bigwaToast.info('Tipo de arquivo não suportado.');
      return;
    }

    // Delegar para importQueueStore — painel assume
    useImportQueueStore.getState().enqueueFiles(allowedFiles);
    setDropState('idle');
  }, []);

  // Drag & drop listeners
  useEffect(() => {
    window.addEventListener('dragenter', handleDragEnter);
    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('drop', handleDrop);

    return () => {
      window.removeEventListener('dragenter', handleDragEnter);
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('drop', handleDrop);
    };
  }, [handleDragEnter, handleDragOver, handleDragLeave, handleDrop]);

  // Escape key closes overlay
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dropState === 'dragging') {
        resetDragState();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dropState, resetDragState]);

  // Cleanup safety timer on unmount
  useEffect(() => {
    return () => {
      if (safetyTimerRef.current) {
        clearTimeout(safetyTimerRef.current);
      }
    };
  }, []);

  // Disambiguation handlers — mantidos para compat com GlobalPdfDropOverlay
  // mas nunca chamados na nova arquitetura (desambiguação é no painel)
  const handleChooseReference = useCallback(() => {
    setDropState('idle');
  }, []);

  const handleChooseResearchData = useCallback(() => {
    setDropState('idle');
  }, []);

  const handleDismissDisambiguate = useCallback(() => {
    setDropState('idle');
  }, []);

  return {
    dropState,
    pendingFile: null as File | null,
    processingProgress: { current: 0, total: 0 },
    handleChooseReference,
    handleChooseResearchData,
    handleDismissDisambiguate,
  };
}
